import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Trophy, Medal, Crown, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANK_STYLE = {
  1: { icon: Crown, color: "text-amber-500", bg: "bg-amber-50 border-amber-300" },
  2: { icon: Medal, color: "text-slate-400", bg: "bg-slate-50 border-slate-300" },
  3: { icon: Medal, color: "text-orange-400", bg: "bg-orange-50 border-orange-200" },
};

export default function LeaderboardPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/leaderboard`).then((r) => setData(r.data)).catch(console.error);
  }, []);

  if (!data) return <main className="max-w-4xl mx-auto px-4 py-24 text-center text-slate-400">Loading leaderboard…</main>;

  return (
    <main data-testid="leaderboard-page" className="max-w-4xl mx-auto px-4 sm:px-8 py-12">
      <p className="uppercase text-xs tracking-[0.2em] font-semibold text-emerald-600 mb-2 flex items-center gap-1.5"><Trophy className="w-4 h-4" /> Top Performer Race — {data.month}</p>
      <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-4">#1 takes home <span className="text-emerald-600">${data.prize}</span>.</h1>
      <p className="text-slate-600 max-w-xl mb-12 leading-relaxed">Every click through your ambassador link counts. The top affiliate by click value each month wins the ${data.prize} Top Performer cash bonus — the board resets on the 1st.</p>

      {data.leaders.length === 0 ? (
        <div data-testid="leaderboard-empty" className="bg-white border border-dashed border-slate-300 rounded-lg p-14 text-center">
          <Trophy className="w-8 h-8 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 text-sm mb-5">No clicks recorded yet this month. The throne is wide open.</p>
          <Link to="/signup" className="inline-block px-7 py-3 rounded-full bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-500 transition-colors duration-200">Join free and claim it</Link>
        </div>
      ) : (
        <div className="space-y-3 mb-12">
          {data.leaders.map((l) => {
            const style = RANK_STYLE[l.rank];
            const Icon = style?.icon;
            return (
              <div key={l.rank} data-testid={`leaderboard-row-${l.rank}`}
                className={`flex items-center gap-4 rounded-lg border px-6 py-4 ${style ? style.bg : "bg-white border-slate-200"} ${l.rank === 1 ? "shadow-md" : ""}`}>
                <span className="font-heading text-2xl font-light text-slate-400 w-8">{l.rank}</span>
                {Icon && <Icon className={`w-5 h-5 ${style.color}`} />}
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-semibold text-sm truncate">{l.name}</p>
                  <p className="text-xs text-slate-500 font-mono">/go/{l.handle}</p>
                </div>
                <span className="text-xs font-semibold text-slate-600 bg-white/70 border border-slate-200 px-3 py-1 rounded-full">{l.tier}</span>
                <div className="text-right">
                  <p className="font-heading text-xl font-medium">{l.clicks.toLocaleString()}</p>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">clicks</p>
                </div>
                {l.rank === 1 && <span data-testid="leaderboard-prize-badge" className="hidden sm:inline text-xs font-bold text-amber-700 bg-amber-100 border border-amber-300 px-3 py-1 rounded-full">${data.prize} leader</span>}
              </div>
            );
          })}
        </div>
      )}

      <div className="relative bg-slate-900 rounded-2xl p-10 overflow-hidden text-center">
        <div className="noise-overlay" />
        <div className="relative">
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight text-white mb-3">Your name belongs up there.</h2>
          <p className="text-slate-400 text-sm mb-6">Join free, grab a ready-made ad, and start climbing today.</p>
          <Link to="/signup" data-testid="leaderboard-join-cta" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-emerald-600 text-white font-medium hover:bg-emerald-500 transition-colors duration-200">
            Join the race <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </main>
  );
}
